/*
 * Mapping Admin Redux Slice
 *
 * Redux state management for Data Mapping Admin functionality
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  getMappingData,
  getMappingDataValues,
  postMappingData,
} from '../../api/mappingAdmin';
import {
  EditModeSnapshot,
  FieldMappingDataRow,
  FixingProxyMappingDataRow,
  LegalEntityMappingDataRow,
  MappingAdminState,
  RowsData,
} from '../../common/domain/mappingAdmin';
import {
  FIELD_NAME,
  SCREEN_MODES,
  ScreenMode,
  Tab,
  TABS,
} from '../../pages/MappingAdminPage/constants';
import { getAuthState } from '../../common/authentication';

const initialState: MappingAdminState = {
  screenMode: SCREEN_MODES.VIEWING_MODE,
  dataGroups: [],
  ratesData: {
    ratesFieldMapping: {},
    ratesFixingMapping: {},
    legalEntityMapping: {},
  },
  loadedTabs: [],
  draftRow: null,
  selectedRows: {
    [TABS.RATES_FIELD]: {},
    [TABS.RATES_FIXING]: {},
    [TABS.LEGAL_ENTITY]: {},
  },
  addedRows: {},
  editedRows: {},
  deletedRows: {},
  validationErrors: {},
  rowEditInProgress: false,
  loading: false,
  groupsLoading: false,
  mappingDataValuesLoading: false,
  saving: false,
  activeTab: TABS.RATES_FIELD,
  errorMessage: null,
  successMessage: null,
  showUpdatedOnly: false,
  mappingDataValues: {
    [FIELD_NAME.DAY_COUNT]: [],
    [FIELD_NAME.PAY_FREQUENCY]: [],
    [FIELD_NAME.REFERENCE_INDEX]: [],
  },
  editModeSnapshot: null,
  rowEditValidationModal: { isOpen: false, message: '' },
};

const getDateWithOffset = (offset: number = 0) => {
  const newDate = new Date();
  newDate.setFullYear(newDate.getFullYear() + offset);
  return newDate.toISOString().slice(0, 19);
};

const mappingAdminSlice = createSlice({
  name: 'mappingAdmin',
  initialState,
  reducers: {
    setScreenMode: (state, action: PayloadAction<ScreenMode>) => {
      state.screenMode = action.payload;
    },

    setActiveTab: (state, action: PayloadAction<Tab>) => {
      state.activeTab = action.payload;
    },

    setUpdatedOnlyFilter: (state, action: PayloadAction<boolean>) => {
      state.showUpdatedOnly = action.payload;
    },

    selectRows: (
      state,
      action: PayloadAction<{
        rowsData:
          | FieldMappingDataRow[]
          | FixingProxyMappingDataRow[]
          | LegalEntityMappingDataRow[];
        tabType: Tab;
      }>,
    ) => {
      const { rowsData, tabType } = action.payload;
      state.selectedRows[tabType] = {};
      for (const rowData of rowsData) {
        state.selectedRows[tabType][rowData.id] = rowData;
      }
    },

    clearSelectedRows: (
      state,
      action: PayloadAction<{
        tabTypes: Tab[];
      }>,
    ) => {
      const { tabTypes } = action.payload;
      tabTypes.forEach((tabType) => {
        state.selectedRows[tabType] = {};
      });
    },

    clearRowChanges: (
      state,
      action: PayloadAction<{
        tabType: Tab;
      }>,
    ) => {
      const { tabType } = action.payload;
      Object.values(state.editedRows).forEach((row) => {
        state.ratesData[tabType][row.oldData.id] = row.oldData;
      });
      Object.values(state.addedRows).forEach((row) => {
        delete state.ratesData[tabType][row.id];
      });
      state.addedRows = {};
      state.editedRows = {};
      state.deletedRows = {};
    },

    addEmptyRow: (
      state,
      action: PayloadAction<{ tabType: Tab; username: string }>,
    ) => {
      const { tabType, username } = action.payload;

      const tempId = `temp_${Date.now()}_${Math.random()}`;
      const currentDateTime = getDateWithOffset();
      const validTo = getDateWithOffset(100);
      let emptyRow = {
        id: tempId,
        validFrom: currentDateTime,
        validTo: validTo,
        lastUpdatedBy: username,
        lastUpdateTimestamp: currentDateTime,
      };

      if (tabType === TABS.RATES_FIELD) {
        emptyRow = {
          ...emptyRow,
          groupId: '',
          fieldName: '',
          inputValue: '',
          mappedValue: '',
        } as FieldMappingDataRow;
      } else if (tabType === TABS.LEGAL_ENTITY) {
        emptyRow = {
          id: tempId,
          legalEntity: '',
          mappedGroupId: '',
          validFrom: currentDateTime,
          validTo: validTo,
          lastUpdatedBy: username,
          lastUpdateTimestamp: currentDateTime,
        } as LegalEntityMappingDataRow;
      } else {
        emptyRow = {
          ...emptyRow,
          groupId: '',
          inputReferenceIndex: '',
          mappedReferenceIndex: '',
          mappedPayFrequency: '',
          mappedDayCount: '',
        } as FixingProxyMappingDataRow;
      }

      state.draftRow = {
        data: emptyRow as
          | FieldMappingDataRow
          | FixingProxyMappingDataRow
          | LegalEntityMappingDataRow,
        tabType,
      };
      state.rowEditInProgress = true;
      const snapshotArr = state.editModeSnapshot?.[tabType];
      if (snapshotArr) snapshotArr.unshift(tempId);
    },

    startEditingRow: (
      state,
      action: PayloadAction<{
        rowData:
          | FieldMappingDataRow
          | FixingProxyMappingDataRow
          | LegalEntityMappingDataRow;
        tabType: Tab;
      }>,
    ) => {
      const { rowData, tabType } = action.payload;

      state.draftRow = {
        data: { ...rowData },
        tabType,
      };
      state.rowEditInProgress = true;
    },

    updateDraftRow: (
      state,
      action: PayloadAction<{ field: string; value: string }>,
    ) => {
      const { field, value } = action.payload;
      if (state.draftRow) {
        (state.draftRow.data as any)[field] = value;
      }
    },

    deleteRow: (state, action: PayloadAction<{ id: string }>) => {
      const { id } = action.payload;
      if (id in state.addedRows) {
        delete state.addedRows[id];
        delete state.ratesData[state.activeTab][id];
      } else {
        if (id in state.editedRows) {
          state.ratesData[state.activeTab][id] = state.editedRows[id].oldData;
          delete state.editedRows[id];
        }
        state.deletedRows[id] = state.ratesData[state.activeTab][id];
      }
    },

    restoreRow: (state, action: PayloadAction<{ id: string }>) => {
      const { id } = action.payload;
      delete state.deletedRows[id];
      const restoreArr = state.editModeSnapshot?.[state.activeTab];
      if (restoreArr) restoreArr.push(id);
    },

    setEditModeSnapshot: (
      state,
      action: PayloadAction<{ snapshot: EditModeSnapshot }>,
    ) => {
      state.editModeSnapshot = action.payload.snapshot;
    },

    clearEditModeSnapshot: (state) => {
      state.editModeSnapshot = null;
    },

    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },

    commitDraftRow: (state, action: PayloadAction<{ username: string }>) => {
      const changesPresent = (
        draftRowData:
          | FieldMappingDataRow
          | FixingProxyMappingDataRow
          | LegalEntityMappingDataRow,
        ratesData: RowsData,
      ) => {
        if (!(draftRowData.id in ratesData)) {
          return true;
        }
        for (const key in draftRowData) {
          if (draftRowData[key] !== ratesData[draftRowData.id][key]) {
            return true;
          }
        }
        return false;
      };

      const { username } = action.payload;

      if (state.draftRow) {
        const { data, tabType } = state.draftRow;
        if (changesPresent(data, state.ratesData[tabType])) {
          data.lastUpdateTimestamp = getDateWithOffset();
          data.lastUpdatedBy = username;
          if (!(data.id in state.ratesData[tabType])) {
            state.addedRows[data.id] = data;
          } else {
            if (data.id in state.editedRows) {
              state.editedRows[data.id].newData = data;
            } else {
              state.editedRows[data.id] = {
                oldData: state.ratesData[tabType][data.id],
                newData: data,
              };
            }
          }
          state.ratesData[tabType][data.id] = data;
        }
        state.draftRow = null;
        state.rowEditInProgress = false;
      }
    },

    addImportedRows: (
      state,
      action: PayloadAction<{ rows: any[]; username: string }>,
    ) => {
      const { rows, username } = action.payload;
      state.addedRows = rows.reduce((data, row) => {
        data[row.id] = {
          ...row,
          lastUpdatedBy: username,
          lastUpdateTimestamp: getDateWithOffset(),
        };
        return data;
      }, {});
    },

    cancelDraftRow: (state) => {
      state.draftRow = null;
      state.rowEditInProgress = false;
      state.validationErrors = {};
    },

    setValidationErrors: (
      state,
      action: PayloadAction<{ rowIds: string[]; fields: string[] }>,
    ) => {
      const { rowIds, fields } = action.payload;
      rowIds.forEach((rowId) => {
        state.validationErrors[rowId] = fields;
      });
    },

    clearValidationErrors: (state) => {
      state.validationErrors = {};
    },

    setRowEditValidationModal: (
      state,
      action: PayloadAction<{ isOpen: boolean; message: string }>,
    ) => {
      state.rowEditValidationModal = action.payload;
    },

    clearValidationErrorForField: (
      state,
      action: PayloadAction<{ rowId: string; field: string }>,
    ) => {
      const { rowId, field } = action.payload;
      if (state.validationErrors[rowId]) {
        state.validationErrors[rowId] = state.validationErrors[rowId].filter(
          (errorField) => errorField !== field,
        );
        if (state.validationErrors[rowId].length === 0) {
          delete state.validationErrors[rowId];
        }
      }
    },

    clearLoadedTabs: (state) => {
      state.loadedTabs = [];
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(loadMappingData.pending, (state) => {
        state.loading = true;
        state.errorMessage = null;
      })
      .addCase(loadMappingData.fulfilled, (state, action) => {
        state.loading = false;
        const { mappingType, data } = action.payload;
        const dataAsObject: {
          [id: string]:
            | FieldMappingDataRow
            | FixingProxyMappingDataRow
            | LegalEntityMappingDataRow;
        } = {};
        data.forEach(
          (
            row:
              | FieldMappingDataRow
              | FixingProxyMappingDataRow
              | LegalEntityMappingDataRow,
          ) => {
            dataAsObject[row.id] = row;
          },
        );

        state.ratesData[mappingType] = dataAsObject;
        if (!state.loadedTabs.includes(mappingType as Tab)) {
          state.loadedTabs.push(mappingType as Tab);
        }
      })
      .addCase(loadMappingData.rejected, (state) => {
        state.loading = false;
        state.errorMessage =
          'An error occurred while loading mapping data. Please try refreshing the page.';
      })
      .addCase(loadGroups.pending, (state) => {
        state.errorMessage = null;
        state.groupsLoading = true;
      })
      .addCase(loadGroups.fulfilled, (state, action) => {
        state.groupsLoading = false;
        state.dataGroups = Array.isArray(action.payload?.groups)
          ? action.payload.groups
          : [];
      })
      .addCase(loadGroups.rejected, (state) => {
        state.groupsLoading = false;
        state.errorMessage = state.errorMessage =
          'An error occurred while loading group ids. Please try refreshing the page.';
      })
      .addCase(saveMappingData.pending, (state) => {
        state.errorMessage = null;
        state.successMessage = null;
        state.saving = true;
      })
      .addCase(saveMappingData.fulfilled, (state) => {
        state.saving = false;
        state.successMessage =
          'Changes were successfully saved. You are going to be returned to the viewing mode.';
      })
      .addCase(saveMappingData.rejected, (state) => {
        console.log('saveMappingData.rejected');
        state.saving = false;
        state.errorMessage = 'Failed to save changes.';
      })
      .addCase(loadMappingDataValues.pending, (state) => {
        state.mappingDataValuesLoading = true;
        state.errorMessage = null;
      })
      .addCase(loadMappingDataValues.fulfilled, (state, action) => {
        state.mappingDataValuesLoading = false;
        const { data } = action.payload;
        state.mappingDataValues = data;
      })
      .addCase(loadMappingDataValues.rejected, (state) => {
        state.mappingDataValuesLoading = false;
        state.errorMessage =
          'An error occurred while loading accepted mapping data values. Please try refreshing the page.';
      });
  },
});

export const loadMappingData = createAsyncThunk(
  'mappingAdmin/loadMappingData',
  async (mappingType: string) => {
    const data = await getMappingData(mappingType);
    return { mappingType, data };
  },
  {
    condition: (mappingType: string, { getState }) => {
      const state = getState() as { mappingAdmin: MappingAdminState };
      const authState = getAuthState();

      const isNotLoading = !state.mappingAdmin.loading;
      const isNotLoaded = !state.mappingAdmin.loadedTabs.includes(
        mappingType as Tab,
      );
      return authState?.isMappingAdmin && isNotLoading && isNotLoaded;
    },
  },
);

export const loadGroups = createAsyncThunk<
  { groups: string[] },
  void,
  { state: { mappingAdmin: MappingAdminState } }
>(
  'mappingAdmin/loadGroups',
  async () => {
    const authState = getAuthState();
    const groups = authState?.userGroups || [];
    return { groups };
  },
  {
    condition: (_, { getState }) => {
      const state = getState() as { mappingAdmin: MappingAdminState };
      const condition =
        state.mappingAdmin.dataGroups.length === 0 &&
        !state.mappingAdmin.groupsLoading;
      return condition;
    },
  },
);

export const loadMappingDataValues = createAsyncThunk(
  'mappingAdmin/loadMappingDataValues',
  async (mappingType: string) => {
    const data = await getMappingDataValues();
    return { mappingType, data };
  },
  {
    condition: (_, { getState }) => {
      const state = getState() as { mappingAdmin: MappingAdminState };
      const {
        mappingDataValuesLoading,
        mappingDataValues,
      } = state.mappingAdmin;
      const isMappingDataValuesEmpty = Object.values(mappingDataValues).every(
        (array) => array.length === 0,
      );
      const condition = !mappingDataValuesLoading && isMappingDataValuesEmpty;
      return condition;
    },
  },
);

export const saveMappingData = createAsyncThunk(
  'mappingAdmin/saveMappingData',
  async (mappingType: string, { getState }) => {
    const state = getState() as { mappingAdmin: MappingAdminState };
    const { addedRows, editedRows, deletedRows } = state.mappingAdmin;

    const response = await postMappingData(
      mappingType,
      addedRows,
      editedRows,
      deletedRows,
    );
    return { mappingType, response };
  },
  {
    condition: (_) => {
      const authState = getAuthState();
      return !!authState?.isMappingAdmin;
    },
  },
);

export const selectIsAnyLoading = (state: {
  mappingAdmin: MappingAdminState;
}) => {
  const {
    loading,
    groupsLoading,
    mappingDataValuesLoading,
  } = state.mappingAdmin;
  const loadingStates = [loading, groupsLoading, mappingDataValuesLoading];
  return loadingStates.some(Boolean);
};

export const {
  setScreenMode,
  setActiveTab,
  setUpdatedOnlyFilter,
  selectRows,
  clearSelectedRows,
  addEmptyRow,
  startEditingRow,
  updateDraftRow,
  deleteRow,
  restoreRow,
  commitDraftRow,
  cancelDraftRow,
  clearRowChanges,
  setValidationErrors,
  clearValidationErrors,
  clearValidationErrorForField,
  setRowEditValidationModal,
  clearLoadedTabs,
  addImportedRows,
  setEditModeSnapshot,
  clearEditModeSnapshot,
  clearSuccessMessage,
} = mappingAdminSlice.actions;

export type { EditModeSnapshot } from '../../common/domain/mappingAdmin';
export default mappingAdminSlice.reducer;
